import type { Component } from 'vue'
import {
  Aim,
  Document,
  MagicStick,
  PictureFilled,
  TrendCharts,
  UserFilled,
  VideoCameraFilled,
} from '@element-plus/icons-vue'

import type { ApprovalStage, WorkflowInstance, WorkflowProgress } from '@/types/workflow'
import { stripWorkflowMarkdown } from '@/utils/workflowRichText'

type CustomerPhaseKey = ApprovalStage

type ProgressLike = {
  progress: number
  status?: WorkflowProgress['status']
}

interface CustomerPhaseDefinition {
  key: CustomerPhaseKey
  title: string
  reviewStage: CustomerPhaseKey
  internalStages: string[]
  baseProgress: number
  pendingSummary: string
  runningSummary: string
  reviewSummary: string
  completedSummary: string
  nextAction: string
}

export interface CustomerPhaseProgress {
  key: CustomerPhaseKey
  title: string
  progress: number
  status: WorkflowProgress['status']
  summary: string
  nextAction: string
  current: boolean
  requiresAttention: boolean
}

export interface ReviewStat {
  label: string
  value: string
}

export interface ReviewIdea {
  title: string
  description: string
  badge?: string
  tone?: 'success' | 'warning' | 'info' | 'primary'
}

export interface ReviewSection {
  key: string
  title: string
  icon: Component
  accent: 'blue' | 'green' | 'violet' | 'rose' | 'amber' | 'slate'
  summary: string
  tags: string[]
  stats?: ReviewStat[]
  ideas?: ReviewIdea[]
  content?: string
}

export interface AgentStep {
  id: string
  title: string
  description: string
  status: 'pending' | 'running' | 'completed'
}

const FAILED_STAGE_TO_APPROVAL_STAGE: Record<string, ApprovalStage> = {
  demand_analysis: 'proposal',
  proposal_generation: 'proposal',
  script_generation: 'script',
  storyboard_generation: 'storyboard',
  asset_generation: 'final',
  video_synthesis: 'final',
  compliance_check: 'final',
  final_packaging: 'final',
}

const WORKFLOW_STATE_LABELS: Record<string, string> = {
  draft: '草稿',
  pending_confirm: '待方案确认',
  confirmed: '方案已确认',
  in_progress: '执行中',
  waiting_script_approval: '待剧本审批',
  waiting_storyboard_approval: '待分镜审批',
  waiting_final_review: '待终审',
  waiting_client_acceptance: '待客户验收',
  revision_requested: '待修改',
  revision_in_progress: '修改中',
  completed: '已完成',
  delivered: '已交付',
  archived: '已归档',
  on_hold: '已暂停',
  failed: '失败',
  cancelled: '已取消',
}

const APPROVAL_STAGE_LABELS: Record<string, string> = {
  proposal: '方案确认',
  script: '剧本审批',
  storyboard: '分镜审批',
  final: '成品验收',
}

const PROGRESS_STATUS_LABELS: Record<string, string> = {
  pending: '未开始',
  running: '进行中',
  review: '待确认',
  completed: '已完成',
  failed: '失败',
  paused: '已暂停',
}

const APPROVAL_STAGE_ORDER: ApprovalStage[] = ['proposal', 'script', 'storyboard', 'final']

const GENERIC_PROPOSAL_TEXTS = ['Generated proposal based on demand analysis']

function looksLikeStructuredJson(value: string): boolean {
  const text = value.trim()
  if (!text) {
    return false
  }

  if (text.startsWith('{') || text.startsWith('[')) {
    return true
  }

  return [
    '"theme"',
    '"genre"',
    '"target_audience"',
    '"main_conflict"',
    '"episode_structure"',
    '"visual_style"',
    '"proposal"',
    '"creative_directions"',
  ].some((marker) => text.includes(marker))
}

function buildDemandNarrative(demand: Record<string, any>): string {
  const paragraphs = [
    [
      normalizeText(demand.theme) ? `当前核心主题聚焦“${normalizeText(demand.theme)}”。` : '',
      normalizeText(demand.target_audience) ? `主要目标用户是${normalizeText(demand.target_audience)}。` : '',
      normalizeText(demand.main_conflict) ? `内容冲突将围绕${normalizeText(demand.main_conflict)}展开。` : '',
    ].filter(Boolean).join(' '),
    [
      normalizeText(demand.setting) ? `建议从${normalizeText(demand.setting)}切入真实使用场景。` : '',
      normalizeText(demand.visual_style) ? `整体视觉风格保持${normalizeText(demand.visual_style)}。` : '',
      normalizeText(demand.tone) ? `表达基调以${normalizeText(demand.tone)}为主。` : '',
    ].filter(Boolean).join(' '),
  ].filter(Boolean)

  const episodeStructure = Array.isArray(demand.episode_structure)
    ? demand.episode_structure.map((item: unknown) => normalizeText(item)).filter(Boolean)
    : []

  if (episodeStructure.length) {
    paragraphs.push(`建议推进节奏：${episodeStructure.join('；')}。`)
  }

  return paragraphs.join('\n\n')
}

function buildProposalNarrative(proposalData: Record<string, any>, demand: Record<string, any>): string {
  const sections = [
    normalizeText(proposalData.market_insight),
    normalizeText(proposalData.audience_profile),
    normalizeText(proposalData.positioning_statement),
  ].filter((item) => item && !looksLikeStructuredJson(item))

  if (sections.length) {
    return sections.join('\n\n')
  }

  return buildDemandNarrative(demand)
}

function formatStoryboardScenesAsText(scenes: Array<Record<string, any>>): string {
  return scenes
    .map((scene, index) => {
      const title = normalizeText(scene.location) || normalizeText(scene.description) || normalizeText(scene.action) || `场景 ${index + 1}`
      const description = normalizeText(scene.description) || normalizeText(scene.action)
      const characters = Array.isArray(scene.characters)
        ? scene.characters.map((item: unknown) => normalizeText(item)).filter(Boolean)
        : []
      const duration = normalizeText(scene.estimated_duration ? `${scene.estimated_duration}` : scene.duration ? `${scene.duration}` : '')
      const dialogueSummary = normalizeText(scene.dialogue_summary)
      const mood = normalizeText(scene.mood)
      const visualCues = Array.isArray(scene.visual_cues)
        ? scene.visual_cues.map((item: unknown) => normalizeText(item)).filter(Boolean)
        : []

      return [
        `### 场景 ${index + 1}：${title}`,
        description,
        characters.length ? `人物：${characters.join('、')}` : '',
        dialogueSummary ? `对白重点：${dialogueSummary}` : '',
        visualCues.length ? `视觉线索：${visualCues.join('、')}` : '',
        mood ? `情绪氛围：${mood}` : '',
        duration ? `建议时长：${duration} 秒` : '',
      ].filter(Boolean).join('\n')
    })
    .filter(Boolean)
    .join('\n\n')
}

const CUSTOMER_PHASES: CustomerPhaseDefinition[] = [
  {
    key: 'proposal',
    title: '需求分析确认',
    reviewStage: 'proposal',
    internalStages: ['proposal_generation'],
    baseProgress: 25,
    pendingSummary: '等待系统开始分析需求与草拟方案。',
    runningSummary: 'AI 正在分析需求、提炼用户画像并形成首轮方案。',
    reviewSummary: '需求分析结果已生成，等待人工确认后继续。',
    completedSummary: '需求分析已确认，系统可以继续进入剧本阶段。',
    nextAction: '剧本生成',
  },
  {
    key: 'script',
    title: '剧本确认',
    reviewStage: 'script',
    internalStages: ['script_generation'],
    baseProgress: 30,
    pendingSummary: '需求分析确认通过后，这里会进入剧本生成。',
    runningSummary: 'AI 正在生成剧本草稿并整理叙事结构。',
    reviewSummary: '剧本结果已生成，等待人工确认后继续。',
    completedSummary: '剧本已确认，系统可以继续进入分镜阶段。',
    nextAction: '分镜生成',
  },
  {
    key: 'storyboard',
    title: '分镜确认',
    reviewStage: 'storyboard',
    internalStages: ['storyboard_generation'],
    baseProgress: 35,
    pendingSummary: '剧本确认通过后，这里会进入分镜生成。',
    runningSummary: 'AI 正在拆解场景、镜头和画面节奏。',
    reviewSummary: '分镜结果已生成，等待人工确认后继续。',
    completedSummary: '分镜已确认，系统可以继续进入制作阶段。',
    nextAction: '素材与视频制作',
  },
  {
    key: 'final',
    title: '成片交付',
    reviewStage: 'final',
    internalStages: ['asset_generation', 'video_synthesis', 'compliance_check', 'final_packaging'],
    baseProgress: 20,
    pendingSummary: '分镜确认通过后，这里会进入素材、视频与交付处理。',
    runningSummary: 'AI 正在准备素材、合成视频并做交付前检查。',
    reviewSummary: '成片结果已生成，等待人工确认后交付。',
    completedSummary: '项目已完成最终确认，可以交付或归档。',
    nextAction: '项目交付',
  },
]

export function getWorkflowStateLabel(status: string): string {
  return WORKFLOW_STATE_LABELS[status] || status
}

export function getApprovalStageLabel(stage: string): string {
  return APPROVAL_STAGE_LABELS[stage] || stage
}

export function getCustomerPhaseLabel(stage: CustomerPhaseKey): string {
  return CUSTOMER_PHASES.find((item) => item.key === stage)?.title || stage
}

export function getProgressStatusLabel(status: WorkflowProgress['status']): string {
  return PROGRESS_STATUS_LABELS[status] || status
}

export function getProgressTagType(status: WorkflowProgress['status']): 'success' | 'warning' | 'danger' | 'info' | 'primary' {
  if (status === 'completed') return 'success'
  if (status === 'review') return 'primary'
  if (status === 'running' || status === 'paused') return 'warning'
  if (status === 'failed') return 'danger'
  return 'info'
}

export function calculateOverallProgress(stageProgress: ProgressLike[]): number {
  if (!stageProgress.length) {
    return 0
  }

  const total = stageProgress.reduce((sum, item) => sum + item.progress, 0)
  return Math.round(total / stageProgress.length)
}

export function getCompletedStageCount(stageProgress: WorkflowProgress[]): number {
  return stageProgress.filter((item) => item.status === 'completed').length
}

export function buildCustomerPhaseProgress(workflow?: WorkflowInstance | null): CustomerPhaseProgress[] {
  const currentKey = getCurrentCustomerPhaseKey(workflow)
  const currentIndex = CUSTOMER_PHASES.findIndex((item) => item.key === currentKey)
  const pendingStage = workflow?.pending_approval?.stage as CustomerPhaseKey | undefined
  const pendingIndex = CUSTOMER_PHASES.findIndex((item) => item.key === pendingStage)
  const completedStages = new Set(workflow?.data?.completed_stages || [])
  const doneStates = new Set(['completed', 'delivered', 'archived'])
  const workflowState = String(workflow?.state || '').trim()
  const hasWorkflowProgress = Boolean(workflow && workflowState && workflowState !== 'draft')

  return CUSTOMER_PHASES.map((phase, index) => {
    const completedInternalCount = phase.internalStages.filter((stage) => completedStages.has(stage)).length
    const allInternalDone = completedInternalCount === phase.internalStages.length
    const isPendingReview = pendingStage === phase.key
    const isCurrent = currentKey === phase.key
    const isDone = !isPendingReview && (
      (hasWorkflowProgress && currentIndex !== -1 && index < currentIndex)
      || (phase.key === 'final' && doneStates.has(workflowState))
      || (allInternalDone && pendingIndex !== -1 && index < pendingIndex)
    )

    let status: WorkflowProgress['status'] = 'pending'
    let progress = 0
    let summary = phase.pendingSummary

    if (isPendingReview) {
      status = 'review'
      progress = phase.key === 'final' ? 92 : 90
      summary = phase.reviewSummary
    } else if (isDone) {
      status = 'completed'
      progress = 100
      summary = phase.completedSummary
    } else if (isCurrent && workflow && workflow.state !== 'draft') {
      const ratio = phase.internalStages.length ? completedInternalCount / phase.internalStages.length : 0
      progress = Math.min(88, Math.max(phase.baseProgress, Math.round(ratio * 100)))

      if (workflow.state === 'failed') {
        status = 'failed'
        progress = Math.max(progress, 25)
        summary = `${phase.title}执行失败，请先排查后再继续。`
      } else if (workflow.state === 'on_hold') {
        status = 'paused'
        summary = `${phase.title}已暂停，等待恢复。`
      } else {
        status = 'running'
        summary = phase.runningSummary
      }
    }

    return {
      key: phase.key,
      title: phase.title,
      progress,
      status,
      summary,
      nextAction: phase.nextAction,
      current: isCurrent,
      requiresAttention: isPendingReview,
    }
  })
}

export function getCurrentCustomerPhase(workflow?: WorkflowInstance | null): CustomerPhaseProgress | null {
  const phases = buildCustomerPhaseProgress(workflow)
  return phases.find((item) => item.current) || phases[0] || null
}

export function getOverallWorkflowProgress(workflow?: WorkflowInstance | null): number {
  return calculateOverallProgress(buildCustomerPhaseProgress(workflow))
}

export function buildProposalAgentSteps(workflow?: WorkflowInstance | null): AgentStep[] {
  const demand = workflow?.data?.demand_analysis_result || {}
  const proposalText = getProposalText(workflow)
  const waitingProposalReview = workflow?.pending_approval?.stage === 'proposal'
  const runningProposal = Boolean(workflow) && !proposalText && !waitingProposalReview

  const finishedDemand = hasMeaningfulText(demand.theme) || hasMeaningfulText(demand.target_audience)
  const finishedProposal = Boolean(proposalText)

  return [
    {
      id: 'market',
      title: '市场分析智能体',
      description: '分析市场机会与竞争环境',
      status: finishedDemand ? 'completed' : runningProposal ? 'running' : 'pending',
    },
    {
      id: 'persona',
      title: '用户画像智能体',
      description: '提炼核心受众与消费动机',
      status: hasMeaningfulText(demand.target_audience) ? 'completed' : runningProposal ? 'running' : 'pending',
    },
    {
      id: 'positioning',
      title: '定位策略智能体',
      description: '锁定产品表达方向与价值主张',
      status: hasMeaningfulText(demand.theme) || hasMeaningfulText(demand.main_conflict) ? 'completed' : runningProposal ? 'running' : 'pending',
    },
    {
      id: 'creative',
      title: '创意生成智能体',
      description: '输出首轮营销创意与提案',
      status: finishedProposal ? 'completed' : runningProposal ? 'running' : 'pending',
    },
  ]
}

export function buildReviewSections(
  workflow: WorkflowInstance | null | undefined,
  stage: 'proposal' | 'script' | 'storyboard' | 'final'
): ReviewSection[] {
  if (!workflow) {
    return []
  }

  if (stage === 'proposal') {
    return finalizeReviewSections(buildProposalSections(workflow))
  }
  if (stage === 'script') {
    return finalizeReviewSections(buildScriptSections(workflow))
  }
  if (stage === 'storyboard') {
    return finalizeReviewSections(buildStoryboardSections(workflow))
  }
  return finalizeReviewSections(buildFinalSections(workflow))
}

export function getReviewPrimaryContent(sections: ReviewSection[]): string {
  for (const section of sections) {
    const content = normalizeText(section.content)
    if (content) {
      return content
    }

    if (section.ideas?.length) {
      return section.ideas
        .map((idea) => [`### ${idea.title}`, idea.description].filter(Boolean).join('\n'))
        .join('\n\n')
    }

    if (section.stats?.length) {
      return section.stats.map((item) => `${item.label}：${item.value}`).join('\n')
    }
  }

  return ''
}

export function getConfirmedStageContent(
  workflow: WorkflowInstance | null | undefined,
  stage: ApprovalStage
): string {
  const confirmed = workflow?.data?.input_data?.stage_confirmed_content as Record<string, unknown> | undefined
  return normalizeText(confirmed?.[stage])
}

function getHighestConfirmedStage(workflow?: WorkflowInstance | null): ApprovalStage | null {
  const persistedHistoricalStage = workflow?.data?.historical_max_stage
  if (persistedHistoricalStage && APPROVAL_STAGE_ORDER.includes(persistedHistoricalStage)) {
    return persistedHistoricalStage
  }

  const confirmed = workflow?.data?.input_data?.stage_confirmed_content as Record<string, unknown> | undefined
  if (!confirmed || typeof confirmed !== 'object') {
    return null
  }

  for (let index = APPROVAL_STAGE_ORDER.length - 1; index >= 0; index -= 1) {
    const stage = APPROVAL_STAGE_ORDER[index]
    if (Object.prototype.hasOwnProperty.call(confirmed, stage)) {
      return stage
    }
  }

  return null
}

export function getHistoricalMaxStage(workflow?: WorkflowInstance | null): ApprovalStage {
  if (hasFinalStageArtifacts(workflow)) {
    return 'final'
  }

  const confirmedStage = getHighestConfirmedStage(workflow)
  if (confirmedStage) {
    return confirmedStage
  }

  if (workflow?.data?.storyboard_generation_result || workflow?.data?.scenes?.length) {
    return 'storyboard'
  }

  if (workflow?.data?.script?.content || workflow?.data?.script_generation_result) {
    return 'script'
  }

  if (workflow?.data?.proposal_generation_result || workflow?.data?.demand_analysis_result) {
    return 'proposal'
  }

  return getPreferredApprovalStage(workflow)
}

function getApprovalStageFromWorkflowState(workflow?: WorkflowInstance | null): ApprovalStage | null {
  const workflowState = normalizeText(String(workflow?.state || ''))

  if (workflowState === 'pending_confirm') {
    return 'proposal'
  }
  if (workflowState === 'waiting_script_approval') {
    return 'script'
  }
  if (workflowState === 'waiting_storyboard_approval') {
    return 'storyboard'
  }
  if (['waiting_final_review', 'waiting_client_acceptance', 'completed', 'delivered', 'archived'].includes(workflowState)) {
    return 'final'
  }

  return null
}

function buildProposalSections(workflow: WorkflowInstance): ReviewSection[] {
  const demand = workflow.data?.demand_analysis_result || {}
  const proposalData = (workflow.data?.proposal_generation_result || {}) as Record<string, any>
  const confirmedProposalContent = getConfirmedStageContent(workflow, 'proposal')
  const fallbackDemandNarrative = buildDemandNarrative(demand)
  const proposalText = getProposalText(workflow) || confirmedProposalContent
  const ideaItems = extractCreativeIdeas(proposalData, proposalText, demand)
  const marketInsight = normalizeText(proposalData.market_insight)
  const audienceProfile = normalizeText(proposalData.audience_profile)
  const positioningStatement = normalizeText(proposalData.positioning_statement)
  const marketSummary = [
    marketInsight,
    hasMeaningfulText(demand.genre) ? `当前内容更适合走${String(demand.genre)}方向。` : '',
    hasMeaningfulText(demand.theme) ? `AI 识别的核心主题是“${String(demand.theme)}”。` : '',
    hasMeaningfulText(demand.main_conflict) ? `主要冲突集中在${String(demand.main_conflict)}。` : '',
  ]
    .filter(Boolean)
    .join(' ')

  const personaSummary = audienceProfile || (hasMeaningfulText(demand.target_audience)
    ? `核心受众为${String(demand.target_audience)}，后续剧本和镜头表达将优先围绕这类人群的兴趣、场景和情绪触发点展开。`
    : '当前还没有明确的人群标签，建议在确认前补充核心受众、使用场景和购买决策因素。')

  const positioningSummary = [
    positioningStatement,
    hasMeaningfulText(demand.theme) ? `建议把产品定位在“${String(demand.theme)}”这一价值主轴上。` : '',
    hasMeaningfulText(demand.visual_style) ? `视觉表达偏向${String(demand.visual_style)}。` : '',
    hasMeaningfulText(demand.tone) ? `整体语气建议保持${String(demand.tone)}。` : '',
  ]
    .filter(Boolean)
    .join(' ')

  return [
    {
      key: 'market',
      title: '市场分析',
      icon: TrendCharts,
      accent: 'blue',
      summary: marketSummary || 'AI 已基于现有需求识别市场方向，确认后将进入剧本生成。',
      tags: uniqueValues([demand.genre, demand.theme, demand.tone, demand.setting]),
      content: marketInsight || fallbackDemandNarrative,
    },
    {
      key: 'persona',
      title: '用户画像',
      icon: UserFilled,
      accent: 'green',
      summary: personaSummary,
      stats: [
        { label: '目标用户', value: formatValue(demand.target_audience, '待补充') },
        { label: '内容语气', value: formatValue(demand.tone, '待补充') },
        { label: '应用场景', value: formatValue(demand.setting, '待补充') },
        { label: '内容形式', value: formatValue(demand.genre, '待补充') },
      ],
      tags: uniqueValues([demand.target_audience, demand.tone, demand.setting]),
      content: audienceProfile || personaSummary,
    },
    {
      key: 'positioning',
      title: '产品定位',
      icon: Aim,
      accent: 'violet',
      summary: positioningSummary || '请确认当前价值主张和视觉风格是否正确，确认后会据此生成剧本。',
      tags: uniqueValues([demand.theme, demand.visual_style, demand.main_conflict, demand.genre]),
      stats: [
        { label: '核心主题', value: formatValue(demand.theme, '待补充') },
        { label: '视觉风格', value: formatValue(demand.visual_style, '待补充') },
        { label: '主要冲突', value: formatValue(demand.main_conflict, '待补充') },
        { label: '建议基调', value: formatValue(demand.tone, '待补充') },
      ],
      content: positioningStatement || positioningSummary,
    },
    {
      key: 'creative',
      title: '营销创意',
      icon: MagicStick,
      accent: 'rose',
      summary: proposalText || 'AI 已形成首轮提案方向，确认后会把这些创意延展为剧本。',
      ideas: ideaItems,
      tags: uniqueValues([demand.theme, demand.visual_style, demand.target_audience]),
      content: proposalText || buildProposalNarrative(proposalData, demand),
    },
  ]
}

function buildScriptSections(workflow: WorkflowInstance): ReviewSection[] {
  const script = workflow.data?.script
  const draft = (workflow.data?.script_generation_result || {}) as Record<string, any>
  const confirmedScriptContent = getConfirmedStageContent(workflow, 'script')
  const content = normalizeText(script?.content || draft.script_content || confirmedScriptContent)
  const intro = content
    ? content.split('\n').find((item) => item.trim()) || content
    : 'AI 已输出剧本草案，请确认主线节奏和角色表达。'

  return [
    {
      key: 'script-overview',
      title: '剧本概览',
      icon: Document,
      accent: 'blue',
      summary: intro,
      tags: uniqueValues([script?.genre, draft.genre, script?.title]),
      stats: [
        { label: '剧本标题', value: formatValue(script?.title, '待生成') },
        { label: '预估时长', value: formatValue(script?.estimated_duration ? `${script.estimated_duration} 分钟` : '', '待补充') },
        { label: '角色数量', value: formatValue(script?.character_count ? `${script.character_count}` : '', '待补充') },
        { label: '场景数量', value: formatValue(script?.scene_count ? `${script.scene_count}` : '', '待补充') },
      ],
    },
    {
      key: 'script-snippet',
      title: '内容预览',
      icon: MagicStick,
      accent: 'amber',
      summary: content || '当前剧本内容为空，请返回执行台查看日志。',
      tags: uniqueValues([workflow.data?.current_focus]),
      content,
    },
  ]
}

function buildStoryboardSections(workflow: WorkflowInstance): ReviewSection[] {
  const storyboard = (workflow.data?.storyboard_generation_result || {}) as Record<string, any>
  const scenes = Array.isArray(storyboard.scenes) ? storyboard.scenes : workflow.data?.scenes || []
  const confirmedStoryboardContent = getConfirmedStageContent(workflow, 'storyboard')
  const editedStoryboardText = normalizeText(storyboard.edited_storyboard_text)
  const locationList = uniqueValues(scenes.map((scene: any) => scene.location || scene.description)).slice(0, 4)
  const sceneContent = editedStoryboardText || formatStoryboardScenesAsText(scenes) || confirmedStoryboardContent
  const overviewSummary = scenes.length
    ? `AI 已生成 ${scenes.length || 0} 个场景的分镜输入，请确认镜头节奏、场景拆分与关键画面是否合理。`
    : (sceneContent ? '当前展示最近一次确认过的分镜旧稿；后续重生成完成后会自动更新为新结果。' : '当前还没有可展示的分镜详情。')

  return [
    {
      key: 'storyboard-overview',
      title: '分镜概览',
      icon: PictureFilled,
      accent: 'violet',
      summary: overviewSummary,
      tags: locationList,
      stats: [
        { label: '场景数量', value: `${scenes.length || 0}` },
        { label: '总镜头数', value: formatValue(storyboard.total_scenes ? `${storyboard.total_scenes}` : '', '待补充') },
      ],
    },
    {
      key: 'storyboard-scenes',
      title: '分镜详情',
      icon: PictureFilled,
      accent: 'amber',
      summary: sceneContent || '当前还没有可展示的分镜详情。',
      tags: locationList,
      content: sceneContent,
    },
  ]
}

function buildFinalSections(workflow: WorkflowInstance): ReviewSection[] {
  const video = workflow.data?.video
  const compliance = workflow.data?.compliance
  const finalPackaging = (workflow.data?.final_packaging_result || {}) as Record<string, any>
  const deliverySummary = normalizeText(finalPackaging.delivery_summary)

  return [
    {
      key: 'final-video',
      title: '成品预览',
      icon: VideoCameraFilled,
      accent: 'blue',
      summary: 'AI 已完成主要生产工作，请在进入交付前确认成片质量、时长和合规状态。',
      tags: uniqueValues([video?.resolution, video?.format]),
      stats: [
        { label: '时长', value: formatValue(video?.duration ? `${video.duration} 秒` : '', '待补充') },
        { label: '分辨率', value: formatValue(video?.resolution, '待补充') },
        { label: '格式', value: formatValue(video?.format, '待补充') },
        { label: '合规结果', value: compliance?.passed ? '通过' : '待确认' },
      ],
      content: deliverySummary,
    },
  ]
}

function getProposalText(workflow?: WorkflowInstance | null): string {
  const proposalData = (workflow?.data?.proposal_generation_result || {}) as Record<string, any>
  const proposal = normalizeText(proposalData.proposal)
  if (!proposal || GENERIC_PROPOSAL_TEXTS.includes(proposal) || looksLikeStructuredJson(proposal)) {
    return ''
  }
  return proposal
}

export function getPreferredApprovalStage(workflow?: WorkflowInstance | null): ApprovalStage {
  const pendingStage = workflow?.pending_approval?.stage
  if (pendingStage) {
    return pendingStage
  }

  const workflowStateStage = getApprovalStageFromWorkflowState(workflow)
  if (workflowStateStage) {
    return workflowStateStage
  }

  const confirmedStage = getHighestConfirmedStage(workflow)
  if (confirmedStage) {
    return confirmedStage
  }

  const lastFailedStage = workflow?.data?.last_failed_stage
  if (lastFailedStage && FAILED_STAGE_TO_APPROVAL_STAGE[lastFailedStage]) {
    return FAILED_STAGE_TO_APPROVAL_STAGE[lastFailedStage]
  }

  const completedStages = new Set(workflow?.data?.completed_stages || [])
  if (!completedStages.has('proposal_generation')) {
    return 'proposal'
  }
  if (!completedStages.has('script_generation')) {
    return 'script'
  }
  if (!completedStages.has('storyboard_generation')) {
    return 'storyboard'
  }
  return 'final'
}

function hasFinalStageArtifacts(workflow?: WorkflowInstance | null): boolean {
  const data = workflow?.data || {}
  const finalDelivery = data.input_data?.storyboard_workspace_state?.final_delivery || {}

  return Boolean(
    data.video
      || data.compliance
      || data.final_packaging_result
      || data.video_synthesis_result?.final_video_url
      || data.video_synthesis_result?.video?.final_video_url
      || finalDelivery.uploaded_final_video?.url
      || finalDelivery.audio_mix_delivery_video?.url
      || finalDelivery.audio_mix_preview_video?.url
      || finalDelivery.mixed_final_video?.url
  )
}

export function getLatestReviewableStage(workflow?: WorkflowInstance | null): ApprovalStage {
  const pendingStage = workflow?.pending_approval?.stage
  if (pendingStage) {
    return pendingStage
  }

  const workflowStateStage = getApprovalStageFromWorkflowState(workflow)
  if (workflowStateStage) {
    return workflowStateStage
  }

  return getHistoricalMaxStage(workflow)
}

export function buildApprovalRoute(projectId: string | number, workflow?: WorkflowInstance | null): string {
  const stage = getLatestReviewableStage(workflow)
  return `/generation/${projectId}?stage=${stage}`
}

function extractCreativeIdeas(proposalData: Record<string, any>, proposalText: string, demand: Record<string, any>): ReviewIdea[] {
  const directIdeas = Array.isArray(proposalData.creative_directions)
    ? proposalData.creative_directions
        .map((item: any, index: number) => {
          if (typeof item === 'string') {
            const description = normalizeText(item)
            if (!description) return null
            return {
              title: `创意方向 ${index + 1}`,
              description,
              badge: index === 0 ? '优先推荐' : '可执行',
              tone: index === 0 ? 'success' : 'info',
            } satisfies ReviewIdea
          }

          const title = normalizeText(item?.title) || `创意方向 ${index + 1}`
          const description = normalizeText(item?.description)
          if (!description) return null
          return {
            title,
            description,
            badge: normalizeText(item?.badge) || (index === 0 ? '优先推荐' : '可执行'),
            tone: (item?.tone as ReviewIdea['tone']) || (index === 0 ? 'success' : 'info'),
          } satisfies ReviewIdea
        })
        .filter(Boolean) as ReviewIdea[]
    : []

  if (directIdeas.length) {
    return directIdeas.slice(0, 4)
  }

  const ideas: ReviewIdea[] = splitSentences(proposalText)
    .filter((item) => item.length >= 8)
    .slice(0, 4)
    .map((item, index) => ({
      title: `创意方向 ${index + 1}`,
      description: item,
      badge: index === 0 ? '优先推荐' : '可执行',
      tone: index === 0 ? 'success' : 'info',
    }))

  if (ideas.length) {
    return ideas
  }

  const theme = formatValue(demand.theme, '核心价值')
  const audience = formatValue(demand.target_audience, '目标用户')
  const style = formatValue(demand.visual_style, '当前风格')

  return [
    {
      title: `${theme}切入`,
      description: `围绕${theme}建立内容主轴，用清晰的利益点快速抓住${audience}的注意力。`,
      badge: '优先推荐',
      tone: 'success',
    },
    {
      title: '用户场景带入',
      description: `从${audience}的真实使用场景切入，把需求冲突和产品价值放在同一条叙事线上。`,
      badge: '高可用',
      tone: 'primary',
    },
    {
      title: '视觉风格统一',
      description: `画面建议保持${style}，确保后续剧本、分镜和素材表达不会偏离当前定位。`,
      badge: '建议保留',
      tone: 'warning',
    },
  ]
}

function splitSentences(value: string): string[] {
  return value
    .split(/[\n。！？；;]+/)
    .map((item) => item.trim())
    .filter(Boolean)
}

function uniqueValues(values: Array<unknown>): string[] {
  return Array.from(
    new Set(
      values
        .map((value) => normalizeText(value))
        .filter(Boolean)
    )
  ).slice(0, 4)
}

function finalizeReviewSections(sections: ReviewSection[]): ReviewSection[] {
  return sections
    .map((section) => {
      const stats = compactReviewStats(section.stats?.map((item) => ({
        label: sanitizeReviewText(item.label),
        value: sanitizeReviewText(item.value),
      })))
      const ideas = section.ideas
        ?.map((idea) => ({
          ...idea,
          title: sanitizeReviewText(idea.title),
          description: sanitizeReviewText(idea.description),
          badge: idea.badge ? sanitizeReviewText(idea.badge) : idea.badge,
        }))
        .filter((idea) => Boolean(idea.title || idea.description || idea.badge))
      const summary = sanitizeReviewText(section.summary)
      return {
        ...section,
        summary,
        tags: section.tags.map((tag) => sanitizeReviewText(tag)).filter(Boolean),
        stats,
        ideas,
      }
    })
    .filter((section) => Boolean(
      normalizeText(section.content)
        || section.ideas?.length
        || section.stats?.length
        || section.tags.length
        || hasMeaningfulSummary(section.summary)
    ))
}

function compactReviewStats(stats?: ReviewStat[]): ReviewStat[] | undefined {
  if (!stats?.length) {
    return undefined
  }

  const filtered = stats.filter((item) => !isPlaceholderValue(item.value))
  return filtered.length ? filtered : undefined
}

function isPlaceholderValue(value: string): boolean {
  return ['待补充', '待生成', '未填写', '暂无'].includes(value)
}

function sanitizeReviewText(value: string): string {
  return stripWorkflowMarkdown(value) || normalizeText(value)
}

function hasMeaningfulSummary(value: string): boolean {
  const summary = normalizeText(value)
  if (!summary) {
    return false
  }

  return !['待补充', '暂无', '当前还没有', '内容为空'].some((item) => summary.includes(item))
}

function formatValue(value: unknown, fallback: string): string {
  const normalized = normalizeText(value)
  return normalized || fallback
}

function hasMeaningfulText(value: unknown): boolean {
  return Boolean(normalizeText(value))
}

function normalizeText(value: unknown): string {
  if (typeof value !== 'string') {
    return ''
  }
  return value.trim()
}

function getCurrentCustomerPhaseKey(workflow?: WorkflowInstance | null): CustomerPhaseKey {
  return getLatestReviewableStage(workflow)
}